import type { TaxConfig, TaxCalculation, CalculationSource } from '../types'

export class TaxCalculator {
  private taxes: TaxConfig[]

  constructor(taxes: TaxConfig[]) {
    this.taxes = taxes.filter(tax => tax.enabled)
  }

  private get totalTaxRate(): number {
    return this.taxes.reduce((sum, tax) => sum + tax.rate, 0)
  }

  private get taxRateMultiplier(): number {
    return 1 + this.totalTaxRate / 100
  }

  parseInputValue(str: string): number {
    if (!str) return 0

    const cleaned = str.replace(/[^\d.-]/g, '')
    const parsed = parseFloat(cleaned)
    return isNaN(parsed) ? 0 : Math.abs(parsed)
  }

  formatValue(val: number): string {
    if (val === null || val === undefined || isNaN(val)) return 'L0.00'
    
    const rounded = Math.round(val * 10000) / 10000
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(rounded)
    return `L${formatted}`
  }

  private computeTaxes(baseAmount: number): Record<string, number> {
    const taxes: Record<string, number> = {}
    this.taxes.forEach(tax => {
      taxes[tax.id] = baseAmount * (tax.rate / 100)
    })
    return taxes
  }

  calculateFromTotal(total: number): TaxCalculation {
    const subtotal = total / this.taxRateMultiplier
    return { total, subtotal, taxes: this.computeTaxes(subtotal) }
  }

  calculateFromSubtotal(subtotal: number): TaxCalculation {
    const total = subtotal * this.taxRateMultiplier
    return { total, subtotal, taxes: this.computeTaxes(subtotal) }
  }

  calculateFromTax(taxId: string, taxAmount: number): TaxCalculation {
    const sourceTax = this.taxes.find(t => t.id === taxId)
    if (!sourceTax) {
      throw new Error(`Tax with ID ${taxId} not found`)
    }

    const subtotal = taxAmount / (sourceTax.rate / 100)
    return this.calculateFromSubtotal(subtotal)
  }

  calculate(source: CalculationSource, value: number): TaxCalculation {
    switch (source) {
      case 'total':
        return this.calculateFromTotal(value)
      case 'subtotal':
        return this.calculateFromSubtotal(value)
      default:
        return this.calculateFromTax(source, value)
    }
  }

  formatCalculation(calculation: TaxCalculation): Record<string, string> {
    const formatted: Record<string, string> = {
      total: this.formatValue(calculation.total),
      subtotal: this.formatValue(calculation.subtotal)
    }

    Object.entries(calculation.taxes).forEach(([taxId, amount]) => {
      formatted[taxId] = this.formatValue(amount)
    })

    return formatted
  }
}